import { useCallback, useEffect, useState } from "react"

interface UseLiveKitAdvancedConfigOptions {
  /** Initial custom-headers JSON string. */
  customHeaders: string
  /** Initial custom-payload JSON string. */
  customPayload: string
  /** Notified with the raw headers string on every change. */
  onCustomHeadersChange?: (value: string) => void
  /** Notified with the raw payload string on every change. */
  onCustomPayloadChange?: (value: string) => void
}

interface UseLiveKitAdvancedConfigResult {
  showAdvanced: boolean
  setShowAdvanced: React.Dispatch<React.SetStateAction<boolean>>
  localHeaders: string
  localPayload: string
  headersError: string | null
  payloadError: string | null
  handleHeadersChange: (value: string) => void
  handlePayloadChange: (value: string) => void
}

/** Returns an error string for invalid JSON, or `null` for empty/valid input. */
function validateJSON(value: string): string | null {
  if (value.trim() === "") {
    return null
  }
  try {
    JSON.parse(value)
    return null
  } catch {
    return "Invalid JSON format - saved as typed"
  }
}

/**
 * Manages the LiveKit "Advanced Configuration" section: the collapse toggle,
 * the local custom-headers/payload text buffers, their inline JSON-validation
 * errors, and the change handlers that mirror edits up to the parent.
 */
export function useLiveKitAdvancedConfig({
  customHeaders,
  customPayload,
  onCustomHeadersChange,
  onCustomPayloadChange,
}: UseLiveKitAdvancedConfigOptions): UseLiveKitAdvancedConfigResult {
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [headersError, setHeadersError] = useState<string | null>(null)
  const [payloadError, setPayloadError] = useState<string | null>(null)

  const [localHeaders, setLocalHeaders] = useState(customHeaders)
  const [localPayload, setLocalPayload] = useState(customPayload)

  useEffect(() => {
    setLocalHeaders(customHeaders)
    setHeadersError(validateJSON(customHeaders))
  }, [customHeaders])

  const handleHeadersChange = useCallback(
    (value: string) => {
      setLocalHeaders(value)
      setHeadersError(validateJSON(value))
      onCustomHeadersChange?.(value)
    },
    [onCustomHeadersChange],
  )

  const handlePayloadChange = useCallback(
    (value: string) => {
      setLocalPayload(value)
      setPayloadError(validateJSON(value))
      onCustomPayloadChange?.(value)
    },
    [onCustomPayloadChange],
  )

  return {
    showAdvanced,
    setShowAdvanced,
    localHeaders,
    localPayload,
    headersError,
    payloadError,
    handleHeadersChange,
    handlePayloadChange,
  }
}
